/**
 * Aviso "próximo paciente" — recepção → médico, em tempo real (RTDB).
 *
 * A secretária, pelo painel lateral da agenda ([[PatientQuickPanel]]), dispara
 * um aviso pro médico de qual é o próximo paciente que vai entrar no
 * consultório. O aviso aparece como POP-UP na tela do médico
 * ([[NextPatientWatcher]]), em qualquer página do sistema — o watcher é global
 * no Shell. É o espelho do [[CallAlertWatcher]] (que avisa a recepção quando o
 * médico chama um paciente), só que no sentido inverso.
 *
 * Efêmero por natureza: vive em `doctorAlerts/{clinicId}/{pushId}` no Realtime
 * Database e some quando o médico dá "Ciente" (ou quando envelhece). O nó é
 * por clínica, mas o aviso leva o `professionalId` do agendamento (= uid do
 * médico) e só pipoca na tela DESSE médico — com vários médicos na clínica,
 * um não vê (nem dá "Ciente" por engano) nos pacientes do outro.
 */
import { rtdb } from './config';
import {
  ref, push, set, onValue, off, remove, serverTimestamp,
  query, orderByChild, limitToLast, DataSnapshot,
} from 'firebase/database';

export interface DoctorAlertData {
  id: string;
  /** Nome do paciente que vai entrar no consultório. */
  patientName: string;
  /** Id do paciente — usado pro pop-up linkar direto na ficha. */
  patientId?: string;
  /** Acompanhante do dia (nome), se informado no agendamento — o médico vê
   *  no pop-up que o paciente vai entrar acompanhado e por quem. */
  accompanistName?: string;
  /** Consultório/sala informada (ex.: "Consultório 1"). */
  room?: string;
  /** Agendamento de origem, se houver. */
  appointmentId?: string;
  /** Médico do agendamento (`professionals/{uid}` → é o uid dele). Só esse
   *  médico vê o pop-up. Obrigatório: o painel não envia sem médico, e aviso
   *  antigo sem o campo não aparece pra ninguém (só é limpo ao envelhecer). */
  professionalId?: string;
  /** Quem avisou (recepção). */
  sentByUid: string;
  sentByName?: string;
  /** serverTimestamp resolvido (ms). */
  createdAt: number;
}

function alertsRef(clinicId: string) {
  return ref(rtdb, `doctorAlerts/${clinicId}`);
}

export const DoctorAlertService = {
  /** Dispara um aviso de "próximo paciente" pra tela do médico. */
  async sendNextPatient(
    clinicId: string,
    payload: {
      patientName: string;
      patientId?: string;
      accompanistName?: string;
      room?: string;
      appointmentId?: string;
      professionalId?: string;
      sentByUid: string;
      sentByName?: string;
    },
  ): Promise<string> {
    const node = push(alertsRef(clinicId));
    // RTDB apaga campos setados como null — vira "ausente", que é o que
    // queremos pra sala/agendamento/nome sem valor.
    await set(node, {
      patientName: payload.patientName,
      patientId: payload.patientId ?? null,
      accompanistName: payload.accompanistName ?? null,
      room: payload.room ?? null,
      appointmentId: payload.appointmentId ?? null,
      professionalId: payload.professionalId ?? null,
      sentByUid: payload.sentByUid,
      sentByName: payload.sentByName ?? null,
      createdAt: serverTimestamp(),
    });
    return node.key!;
  },

  /** Assina os avisos da clínica (lado do médico). Retorna o unsubscribe. */
  subscribe(
    clinicId: string,
    cb: (alerts: DoctorAlertData[]) => void,
  ): () => void {
    const q = query(alertsRef(clinicId), orderByChild('createdAt'), limitToLast(10));
    const handler = (snap: DataSnapshot) => {
      const out: DoctorAlertData[] = [];
      snap.forEach((child) => {
        const v = (child.val() ?? {}) as Partial<DoctorAlertData>;
        out.push({
          id: child.key!,
          patientName: v.patientName ?? 'Paciente',
          patientId: v.patientId ?? undefined,
          accompanistName: v.accompanistName ?? undefined,
          room: v.room ?? undefined,
          appointmentId: v.appointmentId ?? undefined,
          professionalId: v.professionalId ?? undefined,
          sentByUid: v.sentByUid ?? '',
          sentByName: v.sentByName ?? undefined,
          createdAt: typeof v.createdAt === 'number' ? v.createdAt : 0,
        });
      });
      cb(out);
    };
    onValue(q, handler);
    return () => off(q, 'value', handler);
  },

  /** Remove um aviso já visto/confirmado (ou envelhecido). */
  async dismiss(clinicId: string, alertId: string): Promise<void> {
    await remove(ref(rtdb, `doctorAlerts/${clinicId}/${alertId}`));
  },
};
